import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import type { ContentItem } from '../../api/contentApi'

/* eslint-disable @typescript-eslint/no-explicit-any */
type Payload = Record<string, any>

interface GallerySetEditorProps {
  item: ContentItem
  onSave: (payload: Payload) => void
  saving?: boolean
}

/**
 * Editor for a `gallery_set` — one curated mini-collection (3–4 works) belonging to an
 * artist (or room). The curator tunes the name / rationale / blurb; the pieces, pricing
 * and gallery-wall image are computed by the curation engine and shown read-only.
 */
export function GallerySetEditor({ item, onSave, saving }: GallerySetEditorProps) {
  const [p, setP] = useState<Payload>((item.payload as Payload) || {})
  useEffect(() => {
    setP((item.payload as Payload) || {})
  }, [item.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: string, v: any) => setP((prev) => ({ ...prev, [k]: v }))
  const pieces: Payload[] = Array.isArray(p.pieces) ? p.pieces : []
  const traits: Payload = p.cluster_traits || {}
  const minted = p.set_product_handle

  return (
    <div className="space-y-5">
      {p.image_url ? (
        <img src={String(p.image_url)} alt="" className="w-full h-56 object-cover rounded-md border" />
      ) : null}

      <div className="grid grid-cols-2 gap-4">
        <Field label="Set name"><Input value={String(p.name || p.title || '')} onChange={(e) => { set('name', e.target.value); set('title', e.target.value) }} /></Field>
        <Field label="Arrangement"><Input value={String(p.arrangement || '')} onChange={(e) => set('arrangement', e.target.value)} /></Field>
      </div>

      <Field label="Rationale (the shared thread)"><Textarea rows={2} value={String(p.rationale || '')} onChange={(e) => set('rationale', e.target.value)} /></Field>
      <Field label="Description (storefront blurb)"><Textarea rows={3} value={String(p.description || '')} onChange={(e) => set('description', e.target.value)} /></Field>

      <div className="grid grid-cols-3 gap-4">
        <Field label="Composition"><Input value={String(p.composition || '')} onChange={(e) => set('composition', e.target.value)} /></Field>
        <Field label="Price (display)"><Input value={String(p.price || '')} onChange={(e) => set('price', e.target.value)} /></Field>
        <Field label="Per piece"><Input value={String(p.per_piece || '')} onChange={(e) => set('per_piece', e.target.value)} /></Field>
      </div>

      <Field label={`Pieces (${pieces.length}, read-only)`}>
        <div className="space-y-1.5">
          {pieces.map((pc, i) => (
            <div key={i} className="flex items-center justify-between text-sm border rounded-md px-3 py-1.5">
              <span>{pc.title}</span>
              <span className="text-muted-foreground">{pc.price ? `$${pc.price}` : ''}</span>
            </div>
          ))}
        </div>
      </Field>

      {(traits.styles?.length || traits.subjects?.length) ? (
        <Field label="Cluster signature (read-only)">
          <div className="flex flex-wrap gap-1.5">
            {[...(traits.styles || []), ...(traits.subjects || [])].map((t: string) => (
              <span key={t} className="text-xs bg-muted rounded-full px-2 py-0.5">{t}</span>
            ))}
          </div>
        </Field>
      ) : null}

      <Field label="Set product">
        <p className="text-sm text-muted-foreground">
          {minted
            ? `Minted (${p.set_product_status || 'draft'}) → ${minted}`
            : 'Not yet minted — “Shop the Set” uses a cart permalink. Promote to a product to give it a dedicated PDP.'}
        </p>
      </Field>

      <div className="flex justify-end">
        <Button onClick={() => onSave(p)} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  )
}
